import { KitType } from '@repo/types';
import {
  Banner,
  ContainerSizes,
  getSvgByName,
  PageContainer,
  TextColor,
  Typography,
} from '@repo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import conditionsAdult from 'assets/images/conditions-adult.png';
import conditionsBaby from 'assets/images/conditions-baby.png';
import conditionsChild from 'assets/images/conditions-child.png';
import conditionsToddler from 'assets/images/conditions-toddler.png';
import conditionsVaginal from 'assets/images/conditions-vaginal.png';
import {
  ButtonColors,
  ButtonSizes,
  Card,
  LinkButton,
  TextLink,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { findAcknowledgment } from 'helpers/acknowledgments';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useAcknowledgments from 'services/acknowledgments/useAcknowledgments';
import {
  getConditionMetricsListQuery,
  type ConditionWithActionItems,
} from 'services/conditions/useConditionMetrics';
import useFeature from 'services/features/useFeature';
import { AcknowledgmentResource } from 'types/Acknowledgment';
import ConditionMetricCard from './ConditionMetricCard';
import styles from './ConditionMetricsList.module.css';
import { useConditionsEducationalModal } from './ConditionsEducationalModal/context';
import { ConditionsInfoModal } from './ConditionsInfoModal';

export const ConditionMetricsList = () => {
  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const { pathname } = useLocation();

  const { data: conditionData } = useSuspenseQuery<ConditionWithActionItems[]>(
    getConditionMetricsListQuery(currentKitId),
  );

  const { isFeatureEnabled: isHAEMEnabled } = useFeature(
    'health_associations_educational_modal',
  );
  const { isFeatureEnabled: isSensitiveModalEnabled } = useFeature(
    'sensitive_conditions_reminder_modal',
  );

  const { openReview } = useConditionsEducationalModal();

  const { data: acknowledgments } = useAcknowledgments();

  const { trackEvent } = useAnalytics();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('Conditions Page Viewed');
  }, [currentKitId, trackEvent]);

  if (!currentKitId || !currentKit) return <></>;

  const { sampling_type, age_at_sampling } = currentKit;

  let type;
  let imgType;
  if (sampling_type === KitType.VAGINAL) {
    type = 'Vaginal';
    imgType = conditionsVaginal;
  } else if (age_at_sampling! < 365) {
    type = 'Baby';
    imgType = conditionsBaby;
  } else if (age_at_sampling! < 1095) {
    type = 'Toddler';
    imgType = conditionsToddler;
  } else if (age_at_sampling! < 6570) {
    type = 'Child';
    imgType = conditionsChild;
  } else {
    type = 'Adult';
    imgType = conditionsAdult;
  }

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      {!isHAEMEnabled ? <ConditionsInfoModal /> : null}
      <div className='flex flex-column gap-4'>
        <Breadcrumb className={styles.breadcrumb} />
        <Banner
          color='purple'
          title='This test does not diagnose any health conditions.'
          content='These metrics show microbiome associations with health conditions, not causes. Multiple factors including genetics, diet, and lifestyle contribute to risk. This is a wellness test—not a clinical diagnostic—and does not diagnose any condition. Consult your healthcare provider before making health decisions based on this information.'
          icon={getSvgByName('alertTriangleFilled')}
          variant='full'
          size='lg'
          border
        >
          {isHAEMEnabled ? (
            <TextLink
              label='Learn more'
              onClick={openReview}
              color={TextColor.PURPLE}
              className={styles.learnMore}
            />
          ) : null}
        </Banner>
        <Card variant='wrapper' className={styles.cardContainer}>
          <div className={styles.card}>
            <div>
              <div className='flex justify-between align-items-center mb-3'>
                <Typography variant={'heading-s'} className='mb-2'>
                  Metrics related to conditions
                </Typography>
              </div>
              <div className='flex flex-column gap-3 mb-4'>
                <Typography variant={'heading-l'} className={styles.title}>
                  {`${type} conditions & the microbiome`}
                </Typography>
              </div>
            </div>
          </div>
          <div className={styles.conditionMetricsGridListContainer}>
            {conditionData?.map(condition => {
              const acknowledgment = findAcknowledgment(
                acknowledgments,
                AcknowledgmentResource.CONDITIONS_METRICS,
                `${condition.condition_id}`,
                currentTinyAccountId ?? '',
                currentKitId ?? '',
              );

              return (
                <ConditionMetricCard
                  key={condition.condition_id}
                  title={condition.condition_display_name}
                  action_items={condition.action_items_count}
                  link={`${condition.condition}`}
                  evaluationCounts={{
                    great: condition.good,
                    okay: condition.okay,
                    improve: condition.improve,
                    support: condition.support,
                  }}
                  sensitive={
                    condition.sensitive &&
                    !acknowledgment &&
                    isSensitiveModalEnabled
                  }
                />
              );
            })}
          </div>
        </Card>

        <Card variant='wrapper' className={styles.cardContainer}>
          <div>
            <div className='flex justify-between align-items-center mb-3'>
              <Typography variant={'heading-s'}>Emerging Research</Typography>
            </div>
            <div className={styles.researchCardContent}>
              <Typography variant={'heading-s'} className='dark-grey'>
                View the prior emerging research on biomarkers and associations.
              </Typography>
              <TextLink
                label='Read more'
                iconNameRight='chevronForward'
                to={`/results/${currentKitId}/conditions/emerging-research`}
                state={{ from: pathname }}
                color={TextColor.PURPLE}
                className={styles.cardLink}
              />
            </div>
          </div>
        </Card>

        <div className={styles.actionPlanButton}>
          <LinkButton
            href={`/action-plan/${currentKitId}`}
            size={ButtonSizes.M}
            color={ButtonColors.PURPLE}
            label='Go to action plan'
            width={'full'}
          />
        </div>
      </div>
    </PageContainer>
  );
};
